import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getContractorBalanceSheet,
  getProjectPayouts,
} from "../../../services/contractorService";
import {
  renderCurrencyIcon,
  renderCurrencyIconColored,
} from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { store } from "../../../redux/store/configureStore";
import { getContractorId } from "../../../utils/tokenUtils";

// Match the palette / sizing used in
// src/pages/NewProjectDetails/components/ContractorPayments/ProjectStats.js
// so the Settlements analytics row reads identically across the old and
// new project-details views.
const PAID_COLOR = "#4BB34B";       // green
const DUE_COLOR = "#FF971C";        // amber
const NOT_DUE_COLOR = "#C3C6D6";    // gray

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  fontFamily: "Inter Tight",
}));

const formatStatusLabel = (s) =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Bucket sizing mirrors ProjectStats.js — 14px label, 18px value (24px
// for the Total Project Cost large variant), 12px dot, "black-small"
// dirham icon. formattedAmountWithoutAed gives the comma-separated value
// with .00 suffix the screenshot calls for.
const Bucket = ({ label, amount, dotColor, large = false }) => (
  <Box sx={{ flex: 1, minWidth: { xs: 0, md: large ? 180 : 140 } }}>
    <Typography
      sx={{
        fontSize: 14,
        color: "#949DAA",
        fontFamily: "Inter Tight",
        fontWeight: 500,
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {dotColor && (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
      )}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          fontSize: large ? 24 : 18,
          fontWeight: 500,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {renderCurrencyIcon("black-small")}
        {formattedAmountWithoutAed(Number(amount) || 0)}
      </Box>
    </Box>
  </Box>
);

// 10px segmented bar styled to match ProjectStats.js — same height,
// same border radius, same color palette as the buckets above.
const SegmentedProgressBar = ({ segments }) => {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  if (total <= 0) {
    return (
      <Box
        sx={{
          height: 10,
          borderRadius: "5px",
          backgroundColor: "#E5E7EB",
          mt: 1.5,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        height: 10,
        borderRadius: "5px",
        overflow: "hidden",
        backgroundColor: "#E5E7EB",
        mt: 1.5,
      }}
    >
      {segments.map((s, i) => {
        const pct = ((s.value || 0) / total) * 100;
        if (pct <= 0) return null;
        return (
          <Box
            key={i}
            sx={{ width: `${pct}%`, backgroundColor: s.color }}
          />
        );
      })}
    </Box>
  );
};

const StatusPill = ({ status }) => {
  const upper = String(status || "").toUpperCase();
  let dotColor = "#D1D5DB";
  let label = "-";
  if (upper === "PAID" || upper === "RELEASED") {
    dotColor = PAID_COLOR;
    label = formatStatusLabel(status);
  } else if (upper === "DUE" || upper === "OVERDUE") {
    dotColor = DUE_COLOR;
    label = "Due";
  } else if (upper && upper !== "NOT_DUE") {
    dotColor = NOT_DUE_COLOR;
    label = formatStatusLabel(status);
  }
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: dotColor,
        }}
      />
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: label === "-" ? "#6B7280" : "#0D0D0E",
          fontFamily: "Inter Tight",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const formatDate = (val) =>
  val ? moment(val).format("MMM DD, YYYY") : "-";

// Status bucket inferred from row state when the backend doesn't
// pre-bucket. PAID/RELEASED → paid; otherwise if dueDate ≤ today → due;
// future-dated → not due.
const bucketOf = (row) => {
  const s = String(row.status || "").toUpperCase();
  if (s === "PAID" || s === "RELEASED") return "paid";
  if (row.dueDate) {
    const due = moment(row.dueDate).startOf("day");
    const today = moment().startOf("day");
    if (today.isSameOrAfter(due)) return "due";
  }
  return "notDue";
};

// Initial Deposit must always render first regardless of how the backend
// orders the payouts. We detect it by referenceType OR a normalized name
// match — the various endpoints disagree on which field is populated.
const isInitialDepositRow = (r) => {
  const ref = String(r?.referenceType || r?.reference_type || "").toUpperCase();
  if (ref === "PROJECT_INITIAL_DEPOSIT") return true;
  const name = String(
    r?.name ||
      r?.groupName ||
      r?.paymentGroupName ||
      r?.displayName ||
      ""
  )
    .trim()
    .toLowerCase();
  return name === "initial deposit";
};

const sortInitialDepositFirst = (rows) => {
  if (!Array.isArray(rows) || rows.length <= 1) return rows || [];
  const idx = rows.findIndex(isInitialDepositRow);
  if (idx <= 0) return rows;
  return [rows[idx], ...rows.slice(0, idx), ...rows.slice(idx + 1)];
};

const getApiStatusCode = (res) => {
  const code = res?.statusCode ?? res?.status_code ?? res?.code;
  if (code == null) return null;
  const parsed = Number(code);
  return Number.isFinite(parsed) ? parsed : null;
};

// Defensive shape extraction — the balanceSheet response can vary by
// backend version. The current shape returns a `projectBalanceSheet[]`
// array (one entry per project) with per-project totals + a `payouts`
// list. We also fall back to a few flatter shapes seen on older
// backends.
const extractRowsAndTotals = (raw) => {
  const data = raw?.data ?? raw ?? null;
  if (!data) return { rows: [], totals: null };

  // Pick the per-project bucket. The new backend wraps everything in
  // projectBalanceSheet[0] (filtered server-side to the requested
  // projectId); older backends return totals + payouts on the root.
  const sheet =
    (Array.isArray(data.projectBalanceSheet) && data.projectBalanceSheet[0]) ||
    data;

  const candidates = [
    sheet.payouts,
    sheet.contractorPayouts,
    sheet.settlements,
    sheet.lines,
    sheet.paymentLines,
    sheet.rows,
    sheet.items,
    Array.isArray(sheet) ? sheet : null,
  ];
  const rawRows = candidates.find((c) => Array.isArray(c)) || [];

  const rows = rawRows.map((r) => ({
    id: r.id ?? r.payoutId ?? r.lineId,
    name:
      r.groupName ||
      r.displayName ||
      r.paymentGroup?.groupName ||
      r.paymentGroupName ||
      r.name ||
      "",
    dueDate: r.dueDate || r.paymentGroup?.dueDate || r.due_date || null,
    settledOn:
      r.settledOn ||
      r.settled_on ||
      r.settledDate ||
      r.paidDate ||
      r.paymentDate ||
      r.paymentEventTime ||
      null,
    amount: Number(r.amount ?? r.amountWithoutVat ?? 0),
    status: r.status || "",
  }));

  // Totals — prefer backend-provided fields, otherwise compute by bucket.
  const fromApi = {
    totalProjectCost:
      sheet.totalCost ??
      sheet.totalProjectCost ??
      sheet.balance ??
      sheet.totalAmount ??
      sheet.summary?.totalAmount ??
      data.totalBalance ??
      null,
    paid: sheet.totalPaidAmount ?? sheet.summary?.totalPaidAmount ?? null,
    due: sheet.totalDueAmount ?? sheet.summary?.totalDueAmount ?? null,
    notDue:
      sheet.totalNotDueAmount ??
      sheet.summary?.totalNotDueAmount ??
      sheet.totalUnPaidAmount ??
      sheet.summary?.totalUnPaidAmount ??
      null,
  };
  const fallback = rows.reduce(
    (acc, r) => {
      const bucket = bucketOf(r);
      acc.total += r.amount;
      acc[bucket] += r.amount;
      return acc;
    },
    { total: 0, paid: 0, due: 0, notDue: 0 }
  );

  const totals = {
    totalProjectCost: fromApi.totalProjectCost ?? fallback.total,
    paid: fromApi.paid ?? fallback.paid,
    due: fromApi.due ?? fallback.due,
    notDue: fromApi.notDue ?? fallback.notDue,
  };

  return { rows: sortInitialDepositFirst(rows), totals };
};

// Proposal-stage payouts come from /api/v2/payout/project/:projectId.
// Shape is { payouts: [...], totalAmount }. Each row carries
// referenceType (PROJECT_INITIAL_DEPOSIT / PAYMENT_GROUP / etc),
// paymentGroupName, dueDate, amount, status. We surface the same
// {rows, totals} contract the PROJECT-stage extractor returns so the
// render path below stays unchanged.
const extractProposalRowsAndTotals = (raw) => {
  const data = raw?.data ?? raw ?? null;
  if (!data) return { rows: [], totals: null };

  const list = Array.isArray(data.payouts)
    ? data.payouts
    : Array.isArray(data)
    ? data
    : [];

  const rows = list.map((r) => ({
    id: r.id ?? r.payoutId,
    name:
      r.paymentGroupName ||
      r.groupName ||
      r.displayName ||
      r.name ||
      "",
    referenceType: r.referenceType || r.reference_type || "",
    dueDate: r.dueDate || r.due_date || null,
    settledOn: null,
    amount: Number(r.amount ?? r.amountWithoutVat ?? 0),
    status: r.status || "",
  }));

  const fallback = rows.reduce(
    (acc, r) => {
      const bucket = bucketOf(r);
      acc.total += r.amount;
      acc[bucket] += r.amount;
      return acc;
    },
    { total: 0, paid: 0, due: 0, notDue: 0 }
  );

  return {
    rows: sortInitialDepositFirst(rows),
    totals: {
      totalProjectCost: Number(data.totalAmount) || fallback.total,
      paid: fallback.paid,
      due: fallback.due,
      notDue: fallback.notDue,
    },
  };
};

export default function Settlements({ projectId, stage }) {
  const upperStage = String(stage || "").toUpperCase();
  const isProposalStage = upperStage === "PROPOSAL";
  const isRequestStage = upperStage === "REQUEST";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    (async () => {
      // Wait until stage is known to avoid firing the wrong stage API first.
      if (!upperStage) return;

      // REQUEST stage skips network calls — the empty state ("Setup
      // Billing Milestones first") doesn't depend on any payload.
      if (isRequestStage) {
        setRows([]);
        setTotals(null);
        return;
      }
      setLoading(true);
      try {
        if (isProposalStage) {
          const res = await getProjectPayouts(projectId);
          const statusCode = getApiStatusCode(res);
          if (statusCode != null && statusCode !== 2000) {
            toast.error(res?.message || "Failed to load settlements");
            setRows([]);
            setTotals(null);
            return;
          }
          if (cancelled) return;
          const { rows: r, totals: t } = extractProposalRowsAndTotals(res);
          setRows(r);
          setTotals(t);
          return;
        }
        const token = store.getState()?.auth?.token;
        const contractorId = getContractorId(token);
        if (!contractorId) {
          setRows([]);
          setTotals(null);
          return;
        }
        const res = await getContractorBalanceSheet(contractorId, projectId);
        const statusCode = getApiStatusCode(res);
        if (statusCode != null && statusCode !== 2000) {
          toast.error(res?.message || "Failed to load settlements");
          setRows([]);
          setTotals(null);
          return;
        }
        if (cancelled) return;
        const { rows: r, totals: t } = extractRowsAndTotals(res);
        setRows(r);
        setTotals(t);
      } catch (e) {
        console.error("Failed to load settlements", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, isProposalStage, isRequestStage, upperStage]);

  const safeTotals = useMemo(
    () => ({
      totalProjectCost: Number(totals?.totalProjectCost) || 0,
      paid: Number(totals?.paid) || 0,
      due: Number(totals?.due) || 0,
      notDue: Number(totals?.notDue) || 0,
    }),
    [totals]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isRequestStage) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          width: "75%",

        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            color: "#6B7280",
            fontFamily: "Inter Tight",
            fontWeight: 500,
          }}
        >
          Setup Billing Milestones first
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {/* Analytics row: Total Project Cost on the left, then a divider,
          then the three buckets, with a shared segmented progress bar. */}
     {!isProposalStage &&  <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: { xs: 2, md: 4 },
            flexWrap: { xs: "wrap", md: "nowrap" },
            width: { xs: "100%", md: "75%" },
          }}
        >
          <Box sx={{ flex: { xs: "1 1 100%", md: "0 0 auto" }, minWidth: { xs: 0, md: 200 } }}>
            <Bucket
              label="Total Project Cost"
              amount={safeTotals.totalProjectCost}
            />
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: { xs: "grid", md: "flex" },
                gridTemplateColumns: { xs: "1fr 1fr", md: "none" },
                gap: { xs: 1.5, md: 4 },
                flexWrap: "wrap",
              }}
            >
              <Bucket
                label="Paid"
                amount={safeTotals.paid}
                dotColor={PAID_COLOR}
              />
              <Bucket
                label="Due"
                amount={safeTotals.due}
                dotColor={DUE_COLOR}
              />
              <Bucket
                label="Not Due"
                amount={safeTotals.notDue}
                dotColor={NOT_DUE_COLOR}
              />
            </Box>
            <SegmentedProgressBar
              segments={[
                { value: safeTotals.paid, color: PAID_COLOR },
                { value: safeTotals.due, color: DUE_COLOR },
                { value: safeTotals.notDue, color: NOT_DUE_COLOR },
              ]}
            />
          </Box>
        </Box>
      </Box>}

      {rows.length === 0 ? (
        <Typography
          sx={{
            fontSize: 14,
            color: "#9CA3AF",
            fontFamily: "Inter Tight",
            textAlign: "center",
            py: 4,
          }}
        >
          No settlements yet.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            boxShadow: "none",
            backgroundColor: "transparent",
            width: { xs: "100%", md: "75%" },
            display: { xs: "none", md: "block" },
          }}
        >
          <Table
            sx={{
              "& thead th": {
                color: "#9CA3AF",
                fontWeight: 500,
                fontSize: 13,
                fontFamily: "Inter Tight",
                borderBottom: "none",
                paddingTop: "12px",
                paddingBottom: "12px",
              },
              "& tbody tr:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Payment</StyledTableCell>
                <StyledTableCell>Due Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell align="right">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      justifyContent: "flex-end",
                    }}
                  >
                    Amount Due ({renderCurrencyIconColored("black-xs-small")})
                  </Box>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const bucket = bucketOf(r);
                // Status column display: Paid → "Paid"; due → "Due";
                // not-yet-due → "-" (matches the screenshot exactly).
                const displayStatus =
                  bucket === "paid"
                    ? r.status || "Paid"
                    : bucket === "due"
                    ? "DUE"
                    : "";
                return (
                  <TableRow key={r.id ?? `row-${idx}`}>
                    <StyledTableCell sx={{ py: 2 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                        }}
                      >
                        {r.name || "-"}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ fontSize: 13, color: "#6B7280" }}
                    >
                      {formatDate(r.dueDate)}
                    </StyledTableCell>
                       <StyledTableCell>
                      <StatusPill status={displayStatus} />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "flex-end",
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                        }}
                      >
                        {renderCurrencyIconColored("black-xs-small")}
                        {formattedAmountWithoutAed(Number(r.amount) || 0)}
                      </Box>
                    </StyledTableCell>
                 
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile — stacked cards mirroring the settlements table. */}
      {rows.length > 0 && (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
          }}
        >
          {rows.map((r, idx) => {
            const bucket = bucketOf(r);
            const displayStatus =
              bucket === "paid"
                ? r.status || "Paid"
                : bucket === "due"
                ? "DUE"
                : "";
            return (
              <Box
                key={`m-${r.id ?? idx}`}
                sx={{
                  borderBottom: "1px solid #E5E7EB",
                  background: "#fff",
                  py: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.name || "-"}
                  </Typography>
                  {displayStatus && <StatusPill status={displayStatus} />}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Due Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {formatDate(r.dueDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#9CA3AF",
                        fontFamily: "Inter Tight",
                        mb: 0.25,
                      }}
                    >
                      Amount Due
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#0D0D0E",
                        fontFamily: "Inter Tight",
                      }}
                    >
                      {renderCurrencyIconColored("black-xs-small")}
                      {formattedAmountWithoutAed(Number(r.amount) || 0)}
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
